import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { AiFooter } from '../ai-footer';

export type FeatureTileProps = {
	title: string;
	input: ReactNode;
	children: ReactNode;
	tooltip?: ReactNode | null;
	badge?: ReactNode;
	aiFooterRequired?: boolean;
	illustration: ReactNode;
};

export const FeatureTile = ({
	title,
	input,
	children,
	tooltip,
	badge,
	aiFooterRequired,
	illustration,
}: FeatureTileProps) => {
	const toggleId = `FeatureTile-${title.replace(/\s+/g, '-').toLowerCase()}`;

	const imageIcon = <ImageContainer>{illustration}</ImageContainer>;

	return (
		<Wrapper>
			<FlexContainer>
				{imageIcon}
				<Body>
					<Heading as="h5" size="small">
						<Title htmlFor={toggleId}>
							{title}
							{badge && <Badge>{badge}</Badge>}
						</Title>
					</Heading>
					{children}
				</Body>
				{tooltip ? <Tooltip content={tooltip}>{input}</Tooltip> : input}
			</FlexContainer>
			{aiFooterRequired && <AiFooter />}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageContainer = styled.div({
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		width: '56px',
		height: '56px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Badge = styled.div({
	marginLeft: token('space.100', '8px'),
	display: 'inline-block',
	verticalAlign: 'text-bottom',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Body = styled.div({
	flexGrow: 1,
	margin: `0 ${token('space.400', '32px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexContainer = styled.div({
	display: 'flex',
	alignItems: 'start',
	flexGrow: 1,
	padding: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	boxShadow: token(
		'elevation.shadow.raised',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0 1px 1px ${colors.N50A}, 0 0 1px 1px ${colors.N40A}`,
	),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.N40)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize}px`,
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const Title = styled.label({
	display: 'block',
	marginTop: 0,
	marginBottom: token('space.100', '8px'),
});
